import React, { useState } from 'react';
import { Box, Typography, Rating, Button, Fade } from '@mui/material';
import { styles } from '../styles/NPSFeedback.styles';
import { keyframes } from '@mui/system';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
`;

interface NPSFeedbackProps {
  onFeedback: (rating: number) => void;
  onDismiss: () => void;
  conversationId?: string;
}

export const NPSFeedback: React.FC<NPSFeedbackProps> = ({ onFeedback, onDismiss, conversationId }) => {
  const [rating, setRating] = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  if (!conversationId) {
    return null;
  }

  const handleRatingChange = (_: React.SyntheticEvent, newValue: number | null) => {
    setRating(newValue);
    setHoverRating(null);
  };

  const handleRatingHover = (_: React.SyntheticEvent, newValue: number | null) => {
    setHoverRating(newValue);
  };

  const handleSubmit = async () => {
    if (rating !== null) {
      setIsSubmitting(true);
      try {
        await onFeedback(rating);
        setIsExiting(true);
        setTimeout(() => {
          onDismiss();
        }, 500);
      } catch (error) {
        console.error('Erro ao enviar feedback:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss();
    }, 500);
  };

  const getStarColor = (value: number) => {
    const colors = {
      1: '#ff4d4d', 
      2: '#ff8080', 
      3: '#ffb366', 
      4: '#99cc66',
      5: '#4CAF50',
    };
    return colors[value as keyof typeof colors] || '#e0e0e0';
  };

  const currentRating = hoverRating || rating;

  return (
    <Fade in={!isExiting} timeout={500}>
      <Box sx={{
        ...styles.container,
        animation: `${isExiting ? fadeOut : fadeIn} 0.5s ease-out`,
      }}>
        <Typography variant="h6" sx={styles.title}>
          Como foi sua experiência com a LIA?
        </Typography>
        <Typography variant="body2" sx={styles.description}>
          Sua opinião é muito importante para melhorarmos nosso atendimento.
          Se preferir continuar o atendimento, basta começar a digitar.
        </Typography>
        <Box sx={styles.ratingContainer}>
          <Rating
            value={rating}
            onChange={handleRatingChange}
            onChangeActive={handleRatingHover}
            max={5}
            size="large"
            sx={{
              '& .MuiRating-iconFilled': {
                color: (theme) => rating ? getStarColor(rating) : '#e0e0e0',
              },
              '& .MuiRating-iconHover': {
                color: (theme) => hoverRating ? getStarColor(hoverRating) : '#e0e0e0',
              },
              '& .MuiRating-iconEmpty': {
                color: '#e0e0e0',
              },
            }}
          />
        </Box>
        <Box sx={styles.buttonContainer}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={rating === null || isSubmitting}
            sx={{
              ...styles.submitButton,
              transition: 'all 0.3s ease',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
              '&:disabled': {
                opacity: 0.7,
              },
            }}
          >
            {isSubmitting ? 'Enviando...' : 'Enviar Avaliação'}
          </Button>
          <Button
            variant="text"
            onClick={handleDismiss}
            sx={{
              ...styles.dismissButton,
              transition: 'all 0.3s ease',
              color: '#1976d2',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            Continuar Atendimento
          </Button>
        </Box>
      </Box>
    </Fade>
  );
}; 